<template>
  <aside class="drawer">
    <div class="wrapper">
      <header>
        <div>
          <h2>
            Configurar 
            <component :is="getIconByType(node?.type)" class="icon" />
            {{ getNameByType(node?.type) }}
          </h2>
          <a-tooltip v-if="queueDeleted">
            <template #title>
              <div>
                La cola seleccionada fue eliminada, recuerda reemplazarla para poder activar
                nuevamente tu secuencia.
              </div>
            </template>
            <a-tag color="warning" class="header-tag"> <WarningFilled /> Cola eliminada </a-tag>
          </a-tooltip>
        </div>

        <a-button type="text" class="button" @click="handleClose">
          <CloseOutlined />
        </a-button>
      </header>
      <CustomScrollbar height="calc(100% - 45px)">
        <div class="body" :class="{ 'body--fill': node?.type == SequenceNodeType.AutomaticCall }">
          <component
            ref="settingsRef"
            :is="componentToUse"
            :node="cloneDeep(node)"
            :disabled="disabled"
            :sequence="sequence"
          />
        </div>
      </CustomScrollbar>
    </div>

    <footer>
      <a-button @click="handleClose" :disabled="sequenceActive">Cancelar</a-button>
      <a-button type="primary" @click="handleSave" :loading="saving" :disabled="sequenceActive || saveDisabled">
        Guardar
      </a-button>
    </footer>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed, provide, createVNode, watch } from "vue";
import { Modal } from "ant-design-vue";
import { cloneDeep } from "lodash";
import { CloseOutlined, WarningFilled, QuestionCircleOutlined } from "@ant-design/icons-vue";

import { Sequence, SequenceNode, SequenceNodeType, SequenceNodeAction } from "@/@types/flows/sequences";
import { NodeNameByType, NodeTitleByType } from "@/app/flows/sequences/utils/texts";
import { UpdateNodeDto } from "@/@types/flows/sequences/dto";

import TimeIntervalForm from "@/app/flows/sequences/components/sections/drawerForms/TimeIntervalForm.vue";
import AutomaticCallForm from "@/app/flows/sequences/components/sections/drawerForms/AutomaticCallForm.vue";
import EmailForm from "@/app/flows/sequences/components/sections/drawerForms/EmailForm.vue";
import WhatsappForm from "@/app/flows/sequences/components/sections/drawerForms/WhatsappForm.vue";
import SmsForm from "@/app/flows/sequences/components/sections/drawerForms/SmsForm.vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";

import { IconByType } from "@/app/flows/sequences/utils/icons";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FormByType: Record<SequenceNodeType, any> = {
  [SequenceNodeType.TimeInterval]: TimeIntervalForm,
  [SequenceNodeType.AutomaticCall]: AutomaticCallForm,
  [SequenceNodeType.Email]: EmailForm,
  [SequenceNodeType.Whatsapp]: WhatsappForm,
  [SequenceNodeType.Sms]: SmsForm,
};

const props = defineProps<{
  disabled: boolean;
  node: SequenceNode | undefined;
  saving: boolean;
  sequence: Sequence | undefined;
  nodes: SequenceNode[];
}>();

const saveDisabled = ref(true);
provide("saveDisabled", saveDisabled);

const emit = defineEmits<{
  close: [];
  save: [nodeId: number, updatedNode: UpdateNodeDto];
}>();

const handleClose = () => {
  if (saveDisabled.value) {
    emit("close");
  } else {
    Modal.confirm({
      title: "¿Deseas descartar los cambios realizados?",
      content: "Recuerda que si no guardas los cambios registrados, estos se perderán.",
      icon: createVNode(QuestionCircleOutlined),
      onOk: () => {
        emit("close");
      },
      centered: true,
      okText: "Sí, descartar cambios",
      cancelText: "No, seguir editando",
    });
  }
};

const settingsRef = ref<{
  getValidatedFormState: () => Promise<UpdateNodeDto>;
}>();

const componentToUse = computed(() => (props.node ? FormByType[props.node.type] : null));

const queueDeleted = computed(() => {
  return props.node?.queue?.status == 3 && props.node?.queue?.total_users == 0;
});

const getNameByType = (nodeType: SequenceNodeType | undefined) => {
  if (!nodeType) return "desconocido";

  return NodeNameByType[nodeType];
};

const getIconByType = (nodeType: SequenceNodeType | undefined) => {
  if (!nodeType) return null;

  return IconByType[nodeType];
};


const handleSave = async () => {
  if (!settingsRef.value) return;
  if (!props.node) return;

  const formState = await settingsRef.value.getValidatedFormState();
  if (formState.action == SequenceNodeAction.Finish) {
    const index = props.nodes.findIndex((el) => el.id == props.node?.id);
    if (index < props.nodes.length - 1) {
      Modal.confirm({
        icon: createVNode(QuestionCircleOutlined),
        centered: true,
        title: `¿Estás segur@ de guardar esta acción de ${NodeTitleByType[props.node.type]}?`,
        content:
          "Recuerda que se eliminarán todas las acciones inferiores dependientes.",
        okText: "Aceptar",
        onOk: async () => {
          emit("save", props.node?.id, formState);
        }
      });
    } else {
      emit("save", props.node?.id, formState);
    }
    
  } else {
    emit("save", props.node.id, formState);
  }
};

watch(
  () => props.sequence,
  () => {
    console.log('cambia el estado');
    saveDisabled.value = true;
  },
  { immediate: true }
)
const sequenceActive = computed(() => props.sequence?.status != 2);

defineExpose({ saveDisabled });
</script>

<style scoped lang="sass">
.drawer
  display: flex
  flex: 1 1 auto
  max-width: 650px
  flex-direction: column
  justify-content: space-between
  background: #FFFFFF
  // box-shadow: -4px 0px 10px rgba(0, 0, 0, 0.15)
  border-radius: 10px 0 10px 0
  border: 1px solid #00000026
  border-right-width: 0
  border-bottom-width: 0
  overflow: hidden
  // height: calc(100vh - 118px)
  height: calc(100vh - 167px)

.wrapper
  height: calc(100% - 57px)

h2
  color: $gray-9
  font-weight: 600
  font-size: 16px
  margin: 0
  line-height: 24px

.button
  padding: 0 5px
  border-radius: 4px
  height: 24px
  font-size: 16px

header
  border-bottom: 1px solid $gray-4
  padding: 10px 20px
  display: flex
  align-items: center
  justify-content: space-between
  div
    display: flex
    .header-tag
      margin-left: 5px

.body
  padding: 16px 24px
  // Solo para los formularios que anclan contenido al fondo del drawer
  // (ver .automatic_call_alert en AutomaticCallForm). No se aplica a todos
  // porque volver .body un contenedor flex cambia el colapso de márgenes.
  &--fill
    box-sizing: border-box
    min-height: 100%
    display: flex
    flex-direction: column

footer
  border-top: 1px solid $gray-4
  padding: 12px 16px
  display: flex
  align-items: center
  justify-content: flex-end
  gap: 8px

:deep(.icon)
  margin-left: 2px
</style>
